export interface TokenLineMeasurement {
  id: number;
  top: number;
}

export interface VisualLine {
  top: number;
  tokenIds: number[];
}

const LEAD_LINE_PROMOTION_PROGRESS = 0.35;

function sortedMeasurements(measurements: TokenLineMeasurement[]): TokenLineMeasurement[] {
  return [...measurements].sort((left, right) => left.top - right.top || left.id - right.id);
}

function uniqueLineTops(measurements: TokenLineMeasurement[], tolerance: number): number[] {
  const tops: number[] = [];
  const sorted = sortedMeasurements(measurements);

  sorted.forEach((measurement) => {
    if (!tops.some((top) => Math.abs(top - measurement.top) <= tolerance)) {
      tops.push(measurement.top);
    }
  });

  return tops;
}

function lineTokenIds(measurements: TokenLineMeasurement[], top: number, tolerance: number): number[] {
  return sortedMeasurements(measurements)
    .filter((measurement) => Math.abs(measurement.top - top) <= tolerance)
    .map((measurement) => measurement.id);
}

export function groupMeasurementsIntoVisualLines(
  measurements: TokenLineMeasurement[],
  lineHeight: number,
): VisualLine[] {
  const tolerance = Math.max(2, lineHeight * 0.18);
  const lines: VisualLine[] = [];

  sortedMeasurements(measurements).forEach((measurement) => {
    const previous = lines[lines.length - 1];
    if (previous && Math.abs(previous.top - measurement.top) <= tolerance) {
      previous.tokenIds.push(measurement.id);
      return;
    }
    lines.push({ top: measurement.top, tokenIds: [measurement.id] });
  });

  return lines;
}

export function focusedTokenIdsFromVisualLines(
  lines: VisualLine[],
  lineHeight: number,
  scrollTop: number,
  bandTop: number,
  bandBottom: number,
): number[] {
  const minimumTop = scrollTop + bandTop - lineHeight / 2;
  const maximumTop = scrollTop + bandBottom - lineHeight / 2;
  let start = 0;
  let end = lines.length;

  while (start < end) {
    const middle = Math.floor((start + end) / 2);
    if (lines[middle].top < minimumTop) start = middle + 1;
    else end = middle;
  }

  const tokenIds: number[] = [];
  for (let index = start; index < lines.length && lines[index].top <= maximumTop; index += 1) {
    tokenIds.push(...lines[index].tokenIds);
  }
  return tokenIds;
}

export function firstTokenOnVisualLine(
  measurements: TokenLineMeasurement[],
  tokenIndex: number,
  lineHeight: number,
): number {
  const token = measurements.find((measurement) => measurement.id === tokenIndex);
  if (!token) return tokenIndex;
  const tolerance = Math.max(2, lineHeight * 0.18);
  return lineTokenIds(measurements, token.top, tolerance)[0] ?? tokenIndex;
}

export function focusedTokenIdsInFocusBand(
  measurements: TokenLineMeasurement[],
  lineHeight: number,
  scrollTop: number,
  bandTop: number,
  bandBottom: number,
): number[] {
  const centerOffset = lineHeight / 2;
  return measurements
    .filter((measurement) => {
      const center = measurement.top + centerOffset - scrollTop;
      return center >= bandTop && center <= bandBottom;
    })
    .map((measurement) => measurement.id);
}

function leadingLineTop(
  measurements: TokenLineMeasurement[],
  activeTokenIndex: number,
  lineHeight: number,
): number | undefined {
  const active = measurements.find((measurement) => measurement.id === activeTokenIndex);
  if (!active) return undefined;

  const tolerance = Math.max(2, lineHeight * 0.18);
  const tops = uniqueLineTops(measurements, tolerance);
  const currentTop = tops.find((top) => Math.abs(top - active.top) <= tolerance) ?? active.top;
  const nextTop = tops.find((top) => top > currentTop + tolerance);
  if (nextTop === undefined) return currentTop;

  const currentLineIds = lineTokenIds(measurements, currentTop, tolerance);
  const activePosition = currentLineIds.indexOf(activeTokenIndex);
  const activeProgress = currentLineIds.length > 1
    ? activePosition / (currentLineIds.length - 1)
    : 0;

  return activeProgress >= LEAD_LINE_PROMOTION_PROGRESS ? nextTop : currentTop;
}

export function focusedTwoLineTokenIds(
  measurements: TokenLineMeasurement[],
  activeTokenIndex: number,
  lineHeight: number,
): number[] {
  const tolerance = Math.max(2, lineHeight * 0.18);
  const tops = uniqueLineTops(measurements, tolerance);
  const currentTop = leadingLineTop(measurements, activeTokenIndex, lineHeight);
  if (currentTop === undefined) return [activeTokenIndex];
  const nextTop = tops.find((top) => top > currentTop + tolerance);
  const focusedTops = nextTop === undefined ? [currentTop] : [currentTop, nextTop];

  return measurements
    .filter((measurement) => focusedTops.some((top) => Math.abs(top - measurement.top) <= tolerance))
    .map((measurement) => measurement.id);
}

export function leadingTwoLineTokenId(
  measurements: TokenLineMeasurement[],
  activeTokenIndex: number,
  lineHeight: number,
): number {
  const top = leadingLineTop(measurements, activeTokenIndex, lineHeight);
  if (top === undefined) return activeTokenIndex;
  const tolerance = Math.max(2, lineHeight * 0.18);
  return lineTokenIds(measurements, top, tolerance)[0] ?? activeTokenIndex;
}
